import { observable, action, runInAction } from "mobx";
import agent from "../agent";
import profileStore from "./profileStore";
import commonStore from "./commonStore"
import _ from "lodash";
import { Promise } from "bluebird";

import { ProfileType, ProfileRelationType } from "../types/enum";
import userInvitationStore from "./userInvitationStore";
import { emptyRosterFilters, rosterQueryString } from "../helper/actRosterQuery";
import prepareAgentActRosterNames from "../helper/agentActRosterNames";

class AgentActStore {
    @observable pendingAgentActs = [];
    @observable agentActs = [];

    @observable invitedAct = undefined;
    @observable invitationMessage = "";

    @observable invitedActs = [];
    @observable isLoading = false;
    @observable isLoadingPendingActs = false;
    @observable isLoadingAcceptedActs = false;

    @observable actInvitationModalState = false;
    @observable isAgentAct = false;
    @observable invitedChecked = [];
    @observable agentActPage = 1
    @observable isMoreAgentAct = false
    @observable actRosterSearchTerm = ""
    @observable isSearchingActRoster = false
    @observable rosterFilters = emptyRosterFilters();
    @observable rosterError = "";
    rosterRetryPage = 1;
    rosterRetryAppend = false;
    rosterGeneration = 0;
    rosterRequestVersion = 0;
    rosterAgentId = null;
    rosterPageMore = {};
    rosterNeedsReload = false;

    @observable paginationState = {
        currentPage: 1,
        pageSize: 20,
        totalLoaded: 0,
        totalEstimate: null,
        isLoadingAll: false,
        allLoaded: false,
        loadAllProgress: 0,
        loadAllCancelled: false,
        cachedPages: {}
    };

    cacheDuration = 60 * 60 * 1000; // 30 minutes in milliseconds


    @action
    resetData() {
        this.invalidateRosterQuery();
        this.rosterNeedsReload = false;
        this.rosterFilters = emptyRosterFilters();
        this.pendingAgentActs = [];
        this.agentActs = [];
        this.invitedAct = undefined;
        this.invitationMessage = "";
        this.invitedActs = [];
        this.isLoading = false;
        this.agentActPage = 1;
        this.isMoreAgentAct = false;
        this.actRosterSearchTerm = "";
        this.isSearchingActRoster = false;
        this.resetPaginationState();
    }

    @action
    resetPaginationState() {
        this.paginationState = {
            currentPage: 1,
            pageSize: 20,
            totalLoaded: 0,
            totalEstimate: null,
            isLoadingAll: false,
            allLoaded: false,
            loadAllProgress: 0,
            loadAllCancelled: false,
            cachedPages: {}
        };
    }

    @action
    resetAgentActs() {
        this.invalidateRosterQuery();
        this.agentActs = [];
  
    }

    @action
    getAgentAvailableActs(booking, agentId) {
        const { profile } = profileStore;

        if (!agentId) {
            agentId = profile.id;
        }
        return agent.AgentAct.getAgentAvailableActs(agentId, booking).then(
            action(async response => {
                var result = [];
                for (var i = 0; i < response.length; i++) {
                    var data = response[i];
                    await profileStore.mapAdditionalProfileDataAsync(data.act);
                    await profileStore.mapAdditionalProfileDataAsync(data.venue);
                    // profileStore.mapAdditionalProfileData(data.act);
                    // profileStore.mapAdditionalProfileData(data.venue);
                    if (data.act.myMusician) {
                        await profileStore.mapAdditionalProfileDataAsync(data.act.myMusician);
                        // profileStore.mapAdditionalProfileData(data.act.myMusician);
                    }
                    result.push(data);
                }
                result = _.orderBy(result, x=>x.act.actNameString)
               
                this.agentActs = result;
                return this.agentActs;
            })
        );
    }

    @action
    searchAgentActByActName(actName) {
        return this.getAgentActRoster().then(response => {
            var result = [];
            response.forEach(data => {
                if (data.act.fullName.toLowerCase().includes(actName.toLowerCase())) {
                    result.push(data.act);
                }
            });
            return result;
        });
    }

    //Pending Request
    @action
    getAgentsPendingRequest() {
        this.isLoadingPendingActs = true;
        return agent.ProfileRelationship.getAgentActRequest()
            .then(
                action(async response => {
                    this.pendingAgentActs = []
                    for (let data of response) {
                        await profileStore.mapAdditionalProfileData(data.primaryProfile);
                        await profileStore.mapAdditionalProfileData(data.secondaryProfile);

                        if (data.primaryProfile.type === ProfileType.Act.ordinal && data.primaryProfile.myMusician) {
                            await profileStore.mapAdditionalProfileData(data.primaryProfile.myMusician);
                        } else if (data.secondaryProfile.type === ProfileType.Act.ordinal && data.secondaryProfile.myMusician) {
                            await profileStore.mapAdditionalProfileData(data.secondaryProfile.myMusician);
                        }
                        data.venueName = "Loading...";
                        runInAction(() => {
                            this.pendingAgentActs.push(data)
                        })
                    };

                    const venues = response.reduce((x, y) => {
                        if (y && y.rules) {
                            const rules = JSON.parse(y.rules);
                            if (rules.VenueId) {
                                x.push(rules.VenueId);
                            }
                        }
                        return x;
                    }, []);

                    agent.Venue.getSummaries(venues).then(
                        action(response => {
                            runInAction(() => {
                                this.pendingAgentActs.map(pendingAct => {
                                    const rules = JSON.parse(pendingAct.rules);
                                    if (rules && rules.VenueId) {
                                        const venue = response.filter(x => x.id === rules.VenueId);
                                        if (venue && venue.length > 0) {
                                            pendingAct.venueName = venue[0].profileName;
                                        }
                                    }
                                    return pendingAct;
                                });
                            })
                        })
                    );

                    return this.pendingAgentActs;
                })
            )
            .finally(
                action(() => {
                    this.isLoadingPendingActs = false;
                })
            );
    }

    @action
    getActPendingRequest() {
        agent.ProfileRelationship.getActAgentRequest().then(
            action(response => {
                this.pendingAgentActs = response;
            })
        );
    }

    @action
    getMusicianActPendingRequest() {
        return agent.ProfileRelationship.getMusicianActAgentRequest().then(
            action(response => {
                this.pendingAgentActs = response.map(data => {
                    profileStore.mapAdditionalProfileData(data.primaryProfile);
                    profileStore.mapAdditionalProfileData(data.secondaryProfile);

                    if (data.primaryProfile.type === ProfileType.Act.ordinal && data.primaryProfile.myMusician) {
                        profileStore.mapAdditionalProfileData(data.primaryProfile.myMusician);
                    } else if (data.secondaryProfile.type === ProfileType.Act.ordinal && data.secondaryProfile.myMusician) {
                        profileStore.mapAdditionalProfileData(data.secondaryProfile.myMusician);
                    }
                    return data;
                });

                this.pendingAgentActs = _.chain(this.pendingAgentActs)
                    .groupBy("primaryProfileId")
                    .toPairs()
                    .map(function (pair) {
                        return _.zipObject(["agent", "request"], pair);
                    })
                    .value();
                return this.pendingAgentActs;
            })
        );
    }

    @action
    getAgentMyActsForMyGigs(agentId) {
        const { profile } = profileStore;

        if (!agentId) {
            agentId = profile?.id;
        }
        this.isLoadingAcceptedActs = true;
        return agent.AgentAct.getAgentActMyActs(agentId)
            .then(
                action(async response => {
                    // this.agentActs = [];
                    let result = [];

                    for (let data of response) {
                        await this.mapAdditionalProfileData(data.act);
                        await this.mapAdditionalProfileData(data.agent);
                        await this.mapAdditionalProfileData(data.act?.myMusician);
                        runInAction(() => {
                            result.push(data)
                            this.agentActs.push(data)
                        })
                    }
                    return result;
                })
            )
            .finally(
                action(() => {
                    this.isLoadingAcceptedActs = false;
                })
            );
    }

    @action
    getAgentMyActs(agentId) {
        const { profile } = profileStore;

        if (!agentId) {
            agentId = profile?.id;
        }
        this.isLoadingAcceptedActs = true;
        return agent.AgentAct.getAgentActMyActs(agentId)
            .then(
                action(async response => {
                    // this.agentActs = [];
                    for (let data of response) {
                        await this.mapAdditionalProfileData(data.act);
                        await this.mapAdditionalProfileData(data.agent);
                        await this.mapAdditionalProfileData(data.act?.myMusician);
                        runInAction(() => {
                            this.agentActs.push(data)
                        })
                    }   
                    runInAction(() => {
                        this.agentActs = _.orderBy(this.agentActs, x=>x.act.actNameString)    
                    })
                       
                    return this.agentActs;
                })
            )
            .finally(
                action(() => {
                    this.isLoadingAcceptedActs = false;
                })
            );
    }

    //Accepted Request
    @action
    getAgentActRoster(agentId) {
        const { profile } = profileStore;

        if (!agentId) {
            agentId = profile?.id;
        }
        this.isLoadingAcceptedActs = true;
        return agent.AgentAct.getAgentActRoster(agentId)
            .then(
                action(async response => {
                    // this.agentActs = [];
                    for (let data of response) {
                        await this.mapAdditionalProfileData(data.act);
                        await this.mapAdditionalProfileData(data.agent);
                        await this.mapAdditionalProfileData(data.act.myMusician);
                        runInAction(() => {
                            this.agentActs.push(data)
                        })
                    }
                    runInAction(() => {
                        this.agentActs = _.orderBy(this.agentActs, x=>x.act.actNameString)
                    })
                    return this.agentActs;
                })
            )
            .finally(
                action(() => {
                    this.isLoadingAcceptedActs = false;
                })
            );
    }

    @action
    setAgentActPage(page){
        this.agentActPage = page
    }


    @action
    updateAgentActInvoiceType(agentActId, isSwitchOn,profileId){
        var data = {
            invoiceType: isSwitchOn ? 1: 0,
            profileId: profileId
        }
         return agent.AgentAct.updateAgentAct(agentActId,data)
            .then(
                action(async result => {
                    if(result){
                        var agentAct = this.agentActs.filter(x=>x.id === agentActId)
                        if(agentAct && agentAct.length){
                            agentAct[0].invoiceType = data.invoiceType
                        }
                    }
                   
                })
            )
            .finally(
                action(() => {
                    this.isLoadingAcceptedActs = false;
                })
            );
    }

    @action
    updateAgentActInvoiceTypeFromAct(agentActs){
        var data = []
        agentActs.forEach(agentAct => {
            var item = {
                agentActId: agentAct.id,
                invoiceType: agentAct.act.invoiceType
            }
            data.push(item)
        });

         return agent.AgentAct.updateAgentActFromAct(data)
            .then(
                action(async result => {
                    if(result){
                        data.forEach(item => {
                            var selectedResult = result.find(x => x.agentActId == item.agentActId);
                            if(selectedResult){
                                item.isSuccess = selectedResult.isSuccess
                            }
                            
                        });
                        this.agentActs.forEach(agentAct => {
                            var selectedData = data.find(x => x.agentActId == agentAct.id);
                            if(selectedData && selectedData.isSuccess){
                                agentAct.invoiceType = selectedData.invoiceType
                            }
                        });
                         
                    }
                    return result
                   
                })
            )
            .finally(
                action(() => {
                    this.isLoadingAcceptedActs = false;
                })
            );
    }
    @action
    getAgentActRosterWithPagination(agentId,page) {
        return this.loadRosterPage(agentId, page, page > 1);
    }

    @action
    invalidateRosterQuery() {
        this.rosterGeneration += 1;
        this.rosterRequestVersion += 1;
        this.rosterPageMore = {};
        this.isLoadingAcceptedActs = false;
        this.isSearchingActRoster = false;
        this.rosterError = "";
        this.rosterRetryPage = 1;
        this.rosterRetryAppend = false;
        this.resetPaginationState();
    }

    @action
    suspendRosterQuery() {
        const interrupted = this.isLoadingAcceptedActs || this.isSearchingActRoster ||
            this.paginationState.isLoadingAll;
        if (interrupted) {
            this.resetActRosterForReload();
            this.rosterNeedsReload = true;
        } else {
            // Retain a completed page (including its cache/isMore) or Load All
            // view when changing tabs; only invalidate unpublished responses.
            this.rosterGeneration += 1;
            this.rosterRequestVersion += 1;
        }
    }

    @action
    resumeRosterQuery(agentId) {
        if (!this.rosterNeedsReload || !agentId) return Promise.resolve(this.agentActs);
        this.rosterNeedsReload = false;
        return this.loadRosterPage(agentId, 1);
    }

    @action
    setRosterFilters(filters) {
        this.rosterFilters = { ...emptyRosterFilters(), ...filters };
        this.resetActRosterForReload();
    }

    getRosterCriteria = () => ({
        ...this.rosterFilters,
        vibeIds: [...this.rosterFilters.vibeIds],
        genreIds: [...this.rosterFilters.genreIds],
        search: this.actRosterSearchTerm.trim()
    });

    beginRosterRead = agentId => {
        const actor = profileStore.getCurrentProfile()?.id;
        const id = agentId || actor;
        // Do not clear current results or issue old-actor requests from stale callbacks.
        if (actor != null && String(actor) !== String(id)) {
            return { id, criteria: this.getRosterCriteria(), isCurrent: () => false };
        }
        if (String(id) !== String(this.rosterAgentId)) {
            this.invalidateRosterQuery();
            this.agentActs = [];
            this.rosterAgentId = id;
        }
        const generation = this.rosterGeneration;
        const criteria = this.getRosterCriteria();
        const key = rosterQueryString(1, 1, criteria);
        return {
            id, criteria,
            isCurrent: () => generation === this.rosterGeneration &&
                String(this.rosterAgentId) === String(id) &&
                String(profileStore.getCurrentProfile()?.id) === String(actor) &&
                rosterQueryString(1, 1, this.getRosterCriteria()) === key
        };
    };

    fetchRosterRows = async (read, page) => {
        if (!read.isCurrent()) return null;
        if (!read.id) throw new Error("No agent selected.");
        const result = await agent.AgentAct.getAgentActRosterV2(read.id, this.paginationState.pageSize, page, read.criteria);
        if (!read.isCurrent()) return null;
        if (!Array.isArray(result?.data) || typeof result?.isMore !== "boolean") {
            throw new Error("Unable to load the act roster.");
        }
        for (const row of result.data) {
            if (!read.isCurrent()) return null;
            if (row.act) await this.mapAdditionalProfileData(row.act);
            if (row.act && !row.act.lineupCountName && Number(row.act.lineUpCount) >= 1 && Number(row.act.lineUpCount) <= 99) {
                row.act.lineupCountName = this.getLineUpCounts(Number(row.act.lineUpCount));
            }
            if (!read.isCurrent()) return null;
            if (row.agent) await this.mapAdditionalProfileData(row.agent);
            if (!read.isCurrent()) return null;
            if (row.act?.myMusician) await this.mapAdditionalProfileData(row.act.myMusician);
        }
        return read.isCurrent() ? result : null;
    };

    @action
    async loadRosterPage(agentId, page, append = false) {
        const read = this.beginRosterRead(agentId);
        if (!read.isCurrent()) return this.agentActs;
        this.rosterNeedsReload = false;
        const version = ++this.rosterRequestVersion;
        const isCurrent = () => read.isCurrent() && version === this.rosterRequestVersion;
        this.isLoadingAcceptedActs = true;
        this.rosterRetryPage = page;
        this.rosterRetryAppend = append;
        this.isSearchingActRoster = Boolean(read.criteria.search);
        this.rosterError = "";
        try {
            const cached = this.paginationState.cachedPages[page];
            const result = cached ? { data: cached, isMore: this.rosterPageMore[page] } : await this.fetchRosterRows(read, page);
            if (!result || !isCurrent()) return this.agentActs;
            runInAction(() => {
                this.agentActs = append ? _.uniqBy([...this.agentActs, ...result.data], row => row.id) : result.data;
                this.paginationState.cachedPages[page] = result.data;
                this.rosterPageMore[page] = result.isMore;
                this.paginationState.currentPage = page;
                this.paginationState.totalLoaded = Object.values(this.paginationState.cachedPages).flat().length;
                this.agentActPage = page;
                this.isMoreAgentAct = result.isMore;
                this.paginationState.allLoaded = false;
            });
            return this.agentActs;
        } catch (error) {
            if (isCurrent()) runInAction(() => { this.rosterError = "Could not load acts. Please retry."; });
            return this.agentActs;
        } finally {
            if (isCurrent()) runInAction(() => {
                this.isLoadingAcceptedActs = false;
                this.isSearchingActRoster = false;
            });
        }
    }

    @action
    setActRosterSearchTerm(term) {
        if (this.actRosterSearchTerm !== term) {
            this.actRosterSearchTerm = term;
            this.resetActRosterForReload();
        }
    }

    @action
    clearActRosterSearch() {
        this.setActRosterSearchTerm("");
    }

    @action
    resetForNewSearch() {
        this.resetActRosterForReload();
    }

    @action
    searchActRoster(agentId) {
        this.resetForNewSearch();
        return this.loadRosterPage(agentId, 1);
    }

    @action
    searchActRosterNextPage(agentId, page) {
        return this.loadRosterPage(agentId, page, true);
    }

    @action
    resetActRosterForReload() {
        this.invalidateRosterQuery();
        this.rosterNeedsReload = true;
        this.agentActs = [];
        this.agentActPage = 1;
        this.isMoreAgentAct = false;
        this.resetPaginationState();
    }

    @action
    async loadSpecificPage(agentId, pageNum) {
        return this.loadRosterPage(agentId, pageNum);
    }

    @action
    async prefetchNextPage(agentId, pageNum) {
        const read = this.beginRosterRead(agentId);
        if (this.paginationState.cachedPages[pageNum]) return;
        try {
            const result = await this.fetchRosterRows(read, pageNum);
            if (!result || !read.isCurrent()) return;
            runInAction(() => {
                this.paginationState.cachedPages[pageNum] = result.data;
                this.rosterPageMore[pageNum] = result.isMore;
            });
        } catch (error) {
            console.error("Error prefetching page:", error);
        }
    }

    @action
    async loadAllActs(agentId) {
        const { profile } = profileStore;

        if (!agentId) {
            agentId = profile?.id;
        }
        const read = this.beginRosterRead(agentId);
        if (!read.isCurrent()) return this.agentActs;
        this.rosterNeedsReload = false;
        const version = ++this.rosterRequestVersion;
        const isCurrent = () => read.isCurrent() && version === this.rosterRequestVersion;
        this.rosterError = "";

        runInAction(() => {
            this.paginationState.isLoadingAll = true;
            this.paginationState.loadAllCancelled = false;
            this.paginationState.loadAllProgress = 0;
        });

        const { pageSize } = this.paginationState;
        const CONCURRENT_REQUESTS = 4;
        let allActs = [];
        let currentBatchStart = 1;
        let hasMore = true;
        let totalPages = null;

        try {
            while (hasMore && isCurrent() && !this.paginationState.loadAllCancelled) {
                const pageNumbers = [];
                for (let i = 0; i < CONCURRENT_REQUESTS; i++) {
                    pageNumbers.push(currentBatchStart + i);
                }

                const batchPromises = pageNumbers.map(pageNum => 
                    this.fetchRosterRows(read, pageNum)
                        .then(result => ({ pageNum, result }))
                        .catch(error => ({ pageNum, error }))
                );

                const batchResults = await Promise.all(batchPromises);
                if (!isCurrent()) return this.agentActs;
                
                batchResults.sort((a, b) => a.pageNum - b.pageNum);

                let batchHadError = false;
                for (const { pageNum, result, error } of batchResults) {
                    if (this.paginationState.loadAllCancelled || !isCurrent()) break;
                    
                    if (error) {
                        console.error(`Error loading page ${pageNum}:`, error);
                        batchHadError = true;
                        break;
                    }
                    if (!result) return this.agentActs;

                    const response = result?.data || [];
                    
                    if (response.length === 0 && pageNum > 1) {
                        hasMore = false;
                        break;
                    }
                    
                    allActs.push(...response);

                    if (!result?.isMore) {
                        hasMore = false;
                        totalPages = pageNum;
                        break;
                    }
                }

                if (batchHadError) {
                    throw new Error("Failed to load some pages. Please try again.");
                }

                const sortedActs = _.orderBy([...allActs], x => x.act?.actNameString?.toLowerCase());
                const progress = totalPages 
                    ? Math.min(99, (currentBatchStart + CONCURRENT_REQUESTS - 1) / totalPages * 100)
                    : Math.min(90, allActs.length / (allActs.length + pageSize * 2) * 100);

                runInAction(() => {
                    this.agentActs = sortedActs;
                    this.paginationState.totalLoaded = allActs.length;
                    this.paginationState.loadAllProgress = progress;
                });

                currentBatchStart += CONCURRENT_REQUESTS;
            }

            if (isCurrent() && !this.paginationState.loadAllCancelled) {
                const finalSortedActs = _.orderBy(allActs, x => x.act?.actNameString?.toLowerCase());
                runInAction(() => {
                    this.agentActs = finalSortedActs;
                    this.isMoreAgentAct = false;
                    this.paginationState.currentPage = 1;
                    this.paginationState.loadAllProgress = 100;
                    this.paginationState.totalLoaded = finalSortedActs.length;
                    // Keep page cache paginated; the all-results array is not page 1.
                    this.paginationState.cachedPages = {};
                    this.paginationState.allLoaded = true;
                });
            }

            return this.agentActs;
        } catch (error) {
            console.error("Load all failed:", error);
            runInAction(() => {
                if (isCurrent()) {
                    this.paginationState.isLoadingAll = false;
                    this.rosterError = "Could not load all acts. Please retry.";
                }
            });
            return this.agentActs;
        } finally {
            runInAction(() => {
                if (isCurrent()) this.paginationState.isLoadingAll = false;
            });
        }
    }

    @action
    cancelLoadAll() {
        this.rosterRequestVersion += 1;
        this.paginationState.loadAllCancelled = true;
        this.paginationState.isLoadingAll = false;
    }

    @action
    getAgentActRosterSimple(agentId) {
        const { profile } = profileStore;

        if (!agentId) {
            agentId = profile?.id;
        }
        this.isLoadingAcceptedActs = true;
        return agent.AgentAct.getAgentActRosterSimple(agentId)
            .then(
                action(async response => {
                    // this.agentActs = [];
                    for (let data of response) {
                        await this.mapAdditionalProfileData(data.act);
                        await this.mapAdditionalProfileData(data.agent);
                        runInAction(() => {
                            this.agentActs.push(data)
                        })
                    }
                    runInAction(() => {
                        this.agentActs = _.orderBy(this.agentActs, x=>x.act.actNameString)
                    })
                    
                    return this.agentActs;
                })
            )
            .finally(
                action(() => {
                    this.isLoadingAcceptedActs = false;
                })
            );
    }
    @action
    addAgentActListToAgentActs() {
        const { profile } = profileStore;

        return profileStore.getAgentActList()
        .then(
            action(async response => {
                await profileStore.mapAdditionalProfileDataAsync(profile)
                if (response.length > 0) {
                    for(let i = 0; i < response.length; i++) {
                        let act = response[i];
                        act.myMusician = profile;
                        runInAction(() => {
                            let data = {
                                act: act,
                                agent: profile,
                                actId: act.id,
                                agentId: profile.id,
                                id: null,
                                deleted: act.deleted
                            }
                            if (!this.agentActs.find(x => x.actId == data.actId)) {
                                this.agentActs.push(data)
                            }
                        })
                    }
                }
            })
        )
    }

    
    @action
    getAgentActRosterForMyGigs(agentId) {
        const { profile } = profileStore;

        if (!agentId) {
            agentId = profile?.id;
        }

        const cachedData = localStorage.getItem("agentActRoster");
        const cachedTimestamp = localStorage.getItem(`agentActRoster_timestamp`);
        const resetStorage = localStorage.getItem(`resetAgentActRoster`);

        if (cachedData && cachedTimestamp && !resetStorage) {
            const timeElapsed = Date.now() - parseInt(cachedTimestamp, 10);
            if (timeElapsed < this.cacheDuration) {
              this.agentActs = _.orderBy(prepareAgentActRosterNames(JSON.parse(cachedData)), x=>x.act.actNameString)
              return Promise.resolve(this.agentActs);
            }else{
                this.isLoadingAcceptedActs = true;
                return agent.AgentAct.getAgentActRosterSimpleV2(agentId)
                .then(
                    action(response => {
                        // Keep required name preparation, without image processing
                        // or overwriting authoritative V2 names/owner summaries.
                        let result = prepareAgentActRosterNames(response);
                        runInAction(() => {
                            this.agentActs = _.orderBy(result, x=>x.act.actNameString)
                            localStorage.setItem("agentActRoster", JSON.stringify(this.agentActs));
                            localStorage.setItem(`agentActRoster_timestamp`, Date.now().toString());
        
                        })
                        return Promise.resolve(this.agentActs);
                    })
                )
                .finally(
                    action(() => {
                        this.isLoadingAcceptedActs = false;
                    })
                );
            }
        }else{
            if(resetStorage){
                commonStore.resetLocalStorageContainingWords(["agentActRoster", "resetAgentActRoster"])
            }
            this.isLoadingAcceptedActs = true;
            return agent.AgentAct.getAgentActRosterSimpleV2(agentId)
            .then(
                action(response => {
                    let result = prepareAgentActRosterNames(response);
                    runInAction(() => {
                        this.agentActs = _.orderBy(result, x=>x.act.actNameString)
                        localStorage.setItem("agentActRoster", JSON.stringify(this.agentActs));
                        localStorage.setItem(`agentActRoster_timestamp`, Date.now().toString());
    
                    })
                    return Promise.resolve(this.agentActs);
                })
            )
            .finally(
                action(() => {
                    this.isLoadingAcceptedActs = false;
                })
            );
        }


        
    }

    @action
    getConsultantActRoster(consultantId) {
        const { profile } = profileStore;

        if (!consultantId) {
            consultantId = profile.id;
        }

        return agent.AgentAct.getConsultantActRoster(consultantId).then(
            action(async response => {
                // this.agentActs = [];
                for (let data of response) {
                    await this.mapAdditionalProfileData(data.act);
                    await this.mapAdditionalProfileData(data.agent);
                    await this.mapAdditionalProfileData(data.act?.myMusician);
                    runInAction(() => {
                        this.agentActs.push(data)
                    })
                }        
                    this.agentActs = _.orderBy(this.agentActs, x=>x.act.actNameString)         
                return this.agentActs;
            })
        );
    }

    @action
    getConsultantActRosterSimple(consultantId) {
        const { profile } = profileStore;

        if (!consultantId) {
            consultantId = profile.id;
        }

        return agent.AgentAct.getConsultantActRosterSimple(consultantId).then(
            action(async response => {
                // this.agentActs = [];
                for (let data of response) {
                    await this.mapAdditionalProfileData(data.act);
                    await this.mapAdditionalProfileData(data.agent);
                    await this.mapAdditionalProfileData(data.act?.myMusician);
                    runInAction(() => {
                        this.agentActs.push(data)
                    })
                }        
                    this.agentActs = _.orderBy(this.agentActs, x=>x.act.actNameString)         
                return this.agentActs;
            })
        );
    }

    @action
    getActAgents(actId) {
        agent.AgentAct.getActAgents(actId).then(
            action(response => {
                this.agentActs = response;
            })
        );
    }

    @action
    getMusicianAgents(musicianId) {
        const { profile } = profileStore;

        if (!musicianId) {
            musicianId = profile.id;
        }

        return agent.AgentAct.getMusicianAgents(musicianId).then(
            action(async response => {
                if (response && response.length > 0 && response !== undefined) {
                    var mapped = [];
                    for (var i = 0; i < response.length; i++) {
                        var data = response[i]
                        await profileStore.mapAdditionalProfileDataAsync(data.agent);
                        await profileStore.mapAdditionalProfileDataAsync(data.act);
                        data.act.invoiceType = data.invoiceType
                        mapped.push(data)
                    }
                    runInAction(() => {
                        this.agentActs = mapped
                        this.agentActs = _.chain(this.agentActs)
                            .groupBy("agentId")
                            .toPairs()
                            .map(function (pair) {
                                return _.zipObject(["agent", "data"], pair);
                            })
                            .value();
                        return this.agentActs;
                    })
                } else {
                    this.agentActs = []
                    return mapped;
                }
            })
        )
    }

    @action
    acceptInvitation(invitation) {
        const { profile } = profileStore;
        const requestDeatail = {
            primaryProfileId: profile.id,
            secondaryProfileId: Number(invitation.additionalData),
            requestMessage: invitation.invitationMessage,
            type: ProfileRelationType.AgentAct.ordinal,
            createdById: Number(invitation.invitedVenue),
            invitationId: invitation.id
        }
        return agent.ProfileRelationship.invite(requestDeatail).then(
            action(response => {
                this.getAgentsPendingRequest()
            })
        )
    }

    @action
    inviteMany(checkedOnly) {
        const { profile } = profileStore;

        if (this.invitedActs && this.invitedActs.length > 0) {
            var requestDetails = [];
            var listActs = checkedOnly ? this.invitedChecked : this.invitedActs;
            listActs.forEach(invitedAct => {
                requestDetails.push({
                    primaryProfileId: profile.id,
                    secondaryProfileId: invitedAct.id,
                    requestMessage: this.invitationMessage,
                    type: ProfileRelationType.AgentAct.ordinal,
                    createdById: profile.id
                });
            });
            return agent.ProfileRelationship.inviteMany(requestDetails).then(
                action(response => {
                    localStorage.setItem(`resetAgentActRoster`, true);
                    this.resetAgentActs();
                    this.getAgentsPendingRequest();
                    this.getAgentActRosterWithPagination(profile.id, 1);
                    this.getAgentMyActs();
                    userInvitationStore.getMyUserInvitation(ProfileRelationType.AgentAct.ordinal);
                    return this.pendingAgentActs;
                })
            );
        }

        return Promise.resolve();
    }

    @action
    setInvitedChecked(acts) {
        this.invitedChecked = acts;
    }

    @action
    setInvitedActs(acts) {
        this.invitedActs = acts;
    }

    @action
    setInvitationMessage(value) {
        this.invitationMessage = value;
    }

    @action
    setInvitedAct(act) {
        // if the act is coming from the search page, it will be a skeleton act. We will need to load the act details.
        if (!act.myMusician) {
            this.isLoading = true;
            this.invitedAct = []
            return agent.Profile.loadActs(act.userId)
                .then(
                    action(async musician => {
                        await profileStore.mapAdditionalProfileDataAsync(musician.myMusician);
                        if(musician.myActs.length > 0 && Array.isArray(musician.myActs)){
                            for(let i = 0; i < musician.myActs.length; i++) {
                                await profileStore.mapAdditionalProfileDataAsync(musician.myActs[i])
                                musician.myActs[i].selectedActId = act.id
                            }
                        }
                        act.myMusician = musician.myMusician
                        act.myActs = musician.myActs
                        runInAction(() => {
                            this.invitedAct = act;
                            return this.invitedAct
                        })
                    })
                )
                .finally(
                    action(() => {
                        this.isLoading = false;
                    })
                );
        } else {
            profileStore.mapAdditionalProfileData(act.myMusician);
            if (act.myMusician && act.myMusician.myActs) {
                act.myMusician.myActs = act.myMusician.myActs.map(data => {
                    profileStore.mapAdditionalProfileData(data);
                    return data;
                });
            }

            this.invitedAct = act;
            if (!this.invitedActs) {
                this.invitedActs = [];
            }
            this.invitedActs = act.myMusician.myActs
            // this.invitedActs.push(act);
        }
    }

    @action
    clearInviteData() {
        this.invitationMessage = "";
        this.invitedAct = undefined;
        this.invitedActs = [];
    }

    //end of invitation

    @action
    acceptRequest(request) {
        const { currentProfile } = profileStore;
        return agent.ProfileRelationship.accept(request).then(
            action(response => {
                if (currentProfile.type === ProfileType.Musician.ordinal) {
                    this.getMusicianActPendingRequest();
                    this.getMusicianAgents();
                } else if (currentProfile.type === ProfileType.Agent.ordinal) {
                    localStorage.setItem(`resetAgentActRoster`, true);
                    this.resetAgentActs();
                    this.getAgentsPendingRequest();
                    this.getAgentActRoster();
                }
                return response;
            })
        );
    }

    @action
    acceptManyRequest(requests) {
        var data = [];
        requests.forEach(request => {
            data.push(request);
        });
        return agent.ProfileRelationship.acceptMany(data).then(
            action(response => {
                //TODO: delete and add based on response
                this.getMusicianActPendingRequest();
                this.getMusicianAgents();
                return response;
            })
        );
    }

    @action
    removeRequest(request) {
        const { profile } = profileStore;
        return agent.ProfileRelationship.remove(request).then(
            action(response => {
                if (profile.type === ProfileType.Musician.ordinal) {
                    this.getMusicianActPendingRequest();
                    this.getMusicianAgents();
                } else if (profile.type === ProfileType.Agent.ordinal) {
                    this.resetAgentActs();
                    this.getAgentsPendingRequest();
                    this.getAgentActRoster();
                }
                return response;
            })
        );
    }

    @action
    declineRequest(request) {
        const { profile } = profileStore;
        return agent.ProfileRelationship.decline(request).then(
            action(response => {
                if (profile.type === ProfileType.Musician.ordinal) {
                    this.getMusicianActPendingRequest();
                    this.getMusicianAgents();
                } else if (profile.type === ProfileType.Agent.ordinal) {
                    this.resetAgentActs();
                    this.getAgentsPendingRequest();
                    this.getAgentActRoster();
                }
                return response;
            })
        );
    }

    @action
    declineManyRequest(requests) {
        const { profile } = profileStore;
        var data = [];
        requests.forEach(request => {
            data.push(request.id);
        });
        return agent.ProfileRelationship.declineMany(data).then(
            action(response => {
                if (profile.type === ProfileType.Musician.ordinal) {
                    this.getMusicianActPendingRequest();
                    this.getMusicianAgents();
                } else if (profile.type === ProfileType.Agent.ordinal) {
                    this.resetAgentActs();
                    this.getAgentsPendingRequest();
                    this.getAgentActRoster();
                }
                return response;
            })
        );
    }

    @action
    removeAgentAct(agentActId) {
        const { profile } = profileStore;
        return agent.AgentAct.delete(agentActId).then(
            action(response => {
                if (profile.type === ProfileType.Musician.ordinal) {
                    this.getMusicianActPendingRequest();
                    this.getMusicianAgents();
                } else if (profile.type === ProfileType.Agent.ordinal) {
                    localStorage.setItem(`resetAgentActRoster`, true);
                    this.resetAgentActs();
                    this.getAgentsPendingRequest();
                    this.getAgentActRoster();
                    if (!profile.isConsultant){
                        this.addAgentActListToAgentActs()
                    }
                }
            })
        );
    }

    @action
    removeAgentActs(agentActs) {
        const { profile } = profileStore;

        var data = [];
        agentActs.forEach(agentAct => {
            data.push(agentAct.id);
        });

        return agent.AgentAct.deleteMany(data).then(
            action(response => {
                if (profile.type === ProfileType.Musician.ordinal) {
                    this.getMusicianActPendingRequest();
                    this.getMusicianAgents();
                } else if (profile.type === ProfileType.Agent.ordinal) {
                    localStorage.setItem(`resetAgentActRoster`, true);
                    this.resetAgentActs();
                    this.getAgentsPendingRequest();
                    this.getAgentActRoster();
                }
            })
        );
    }

    @action
    getLineUpCounts(instrument) {
        if (!instrument || instrument === 0) {
            return "";
        }

        let lineUpCounts = [
            { name: "Solo", instrument: 1 },
            { name: "Duo", instrument: 2 },
            { name: "Trio", instrument: 3 },
            { name: "Quartet / 4 Piece", instrument: 4 },
            { name: "Quintet / 5 Piece", instrument: 5 },
            { name: "Sextet / 6 Piece", instrument: 6 }
        ];

        for (var i = 0; i < 93; i++) {
            lineUpCounts.push({ name: i + 7 + " Piece", instrument: i + 7 });
        }

        return lineUpCounts.filter(lineUpCount => lineUpCount.instrument === instrument)[0].name;
    }

    @action
    openActInvitationModal() {
        this.actInvitationModalState = true;
        this.clearInviteData();
    }

    @action
    closeActInvitationModal() {
        this.actInvitationModalState = false;
    }

    @action
    async mapAdditionalProfileData(profile) {
        await profileStore.mapAdditionalProfileImageAndBackgroundImages(profile);
        profileStore.mapAdditionalProfileNames(profile);
    }

    @action
    setIsAgentAct(value) {
        this.isAgentAct = value;
    }
}

export default new AgentActStore();
